/**
 * ClickReel SDK - Reel Trigger UI
 *
 * Renders the floating bubble or pill trigger button
 * with hover/click states and mobile responsiveness
 */

import { BubblePosition, MorphTarget, ShakeIntensity, SocialProofConfig, StylingConfig, VideoConfig } from './types';
import { getShadowContainer } from './shadow-dom';
import {
  getThumbnailFallbackChain,
  injectUtilityStyles,
  isGradientMarker,
  isMobile,
  loadYouTubeApi,
  parseGradientMarker,
  type ThumbnailPlatform,
} from './video-utils';
import { ensureGlobalMorphKeyframes } from './morph-keyframes';

// =============================================================================
// Sprint 001 (2026-04-29): Thumbnail rendering helpers
//
// The bubble previously rendered the thumbnail as a `background-image` div,
// which gave us no `error` event when the URL 404'd — leaving an empty
// container in the bubble outline. The helpers below render an <img> with a
// chained `onerror` cascade that falls back through alternate URLs and finally
// to a branded gradient + platform icon.
// =============================================================================

/**
 * Branded gradient + icon HTML for the platform. Generalised from the prior
 * Instagram/TikTok-only inline block in createButton(). Used as the final
 * fallback layer when every URL in the thumbnail chain has 404'd.
 */
function getBrandedFallbackStyles(platform: ThumbnailPlatform): { gradient: string; shadow: string } {
  switch (platform) {
    case 'instagram':
      return {
        gradient: 'linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
        shadow: 'rgba(225, 48, 108, 0.4)',
      };
    case 'tiktok':
      return {
        gradient: 'linear-gradient(135deg, #25F4EE, #FE2C55, #000)',
        shadow: 'rgba(254, 44, 85, 0.4)',
      };
    case 'youtube':
      return {
        gradient: 'linear-gradient(135deg, #FF0000, #CC0000, #1a0000)',
        shadow: 'rgba(255, 0, 0, 0.4)',
      };
    case 'cloudflare':
      return {
        gradient: 'linear-gradient(135deg, #F38020, #FBAD41, #2C7CB0)',
        shadow: 'rgba(243, 128, 32, 0.4)',
      };
    case 'html5':
    default:
      return {
        gradient: 'linear-gradient(160deg, #1a1a2e, #16213e, #0f3460)',
        shadow: 'rgba(15, 52, 96, 0.4)',
      };
  }
}

/**
 * SVG icon centered on the branded gradient fallback. Generic play icon for
 * platforms that don't have a recognisable monogram (HTML5, Cloudflare).
 */
function getBrandedFallbackIcon(platform: ThumbnailPlatform, size: number): string {
  const dim = Math.round(size * 0.4);
  switch (platform) {
    case 'instagram':
      return `<svg width="${dim}" height="${dim}" viewBox="0 0 24 24" fill="white" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>`;
    case 'tiktok':
      return `<svg width="${dim}" height="${dim}" viewBox="0 0 24 24" fill="white" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.18 8.18 0 004.76 1.52V6.84a4.84 4.84 0 01-1-.15z"/></svg>`;
    case 'youtube':
      return `<svg width="${dim}" height="${dim}" viewBox="0 0 24 24" fill="white" aria-hidden="true"><path d="M21.582 6.186a2.506 2.506 0 00-1.768-1.768C18.254 4 12 4 12 4s-6.254 0-7.814.418A2.506 2.506 0 002.418 6.186C2 7.746 2 12 2 12s0 4.254.418 5.814a2.506 2.506 0 001.768 1.768C5.746 20 12 20 12 20s6.254 0 7.814-.418a2.506 2.506 0 001.768-1.768C22 16.254 22 12 22 12s0-4.254-.418-5.814zM10 15.5v-7l6 3.5-6 3.5z"/></svg>`;
    case 'cloudflare':
    case 'html5':
    default:
      return `<svg width="${dim}" height="${dim}" viewBox="0 0 24 24" fill="white" aria-hidden="true"><path d="M8 5.14v13.72c0 .89.97 1.44 1.71.97l10.49-6.86a1.16 1.16 0 000-1.94L9.71 4.17A1.16 1.16 0 008 5.14z"/></svg>`;
  }
}

/**
 * Build a branded fallback layer (gradient + centered icon). Sized to fill
 * its parent (`position: absolute; inset: 0`) so it can swap in cleanly when
 * an image errors out.
 */
function renderBrandedFallback(platform: ThumbnailPlatform, size: number): HTMLElement {
  const { gradient } = getBrandedFallbackStyles(platform);
  const layer = document.createElement('div');
  layer.className = `clickreel-branded-fallback clickreel-branded-fallback-${platform}`;
  layer.style.cssText = `
    position: absolute;
    inset: 0;
    background: ${gradient};
    border-radius: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
  `;
  const iconWrap = document.createElement('div');
  iconWrap.style.cssText = 'opacity: 0.92; pointer-events: none;';
  iconWrap.innerHTML = getBrandedFallbackIcon(platform, size);
  layer.appendChild(iconWrap);
  return layer;
}

/**
 * Render an <img>-based thumbnail with a chained onerror cascade. When every
 * URL in the chain fails (or the chain is just a single gradient marker), the
 * img is removed and replaced with the branded fallback layer.
 *
 * The skeleton shimmer layer beneath the img is removed on first successful
 * `load` OR when we swap to the branded fallback — guaranteeing no transparent
 * gap from container paint until final render.
 */
function renderThumbnailWithFallback(opts: {
  chain: string[];
  bubbleSize: number;
  alt?: string;
  imgClassName?: string;
  /** Extra CSS to apply on the <img>. Must not include `background-image`. */
  imgStyle?: string;
}): HTMLElement {
  const { chain, bubbleSize, alt = '', imgClassName = 'clickreel-thumbnail', imgStyle = '' } = opts;
  const wrapper = document.createElement('div');
  wrapper.className = 'clickreel-thumbnail-wrapper';
  wrapper.style.cssText = `
    position: absolute;
    inset: 0;
    border-radius: inherit;
    overflow: hidden;
    z-index: 1;
  `;

  // Skeleton shimmer — visible from container paint until first load OR the
  // final fallback swap. Styled via .clickreel-thumbnail-skeleton in CSS.
  const skeleton = document.createElement('div');
  skeleton.className = 'clickreel-thumbnail-skeleton';
  skeleton.setAttribute('aria-hidden', 'true');
  wrapper.appendChild(skeleton);

  // If the chain is empty or starts immediately with a gradient marker, skip
  // <img> entirely and render the branded fallback.
  const startsWithGradient = chain.length === 0 || isGradientMarker(chain[0]);
  if (startsWithGradient) {
    const platform: ThumbnailPlatform =
      chain.length > 0 ? parseGradientMarker(chain[0]) : 'html5';
    skeleton.remove();
    wrapper.appendChild(renderBrandedFallback(platform, bubbleSize));
    return wrapper;
  }

  const img = document.createElement('img');
  img.className = imgClassName;
  img.alt = alt;
  // Important: object-fit:cover replicates the prior `background-size:cover`
  // behaviour. The wrapper provides border-radius via `inherit`.
  img.style.cssText = `
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: inherit;
    display: block;
    opacity: 0;
    transition: opacity 0.18s ease;
    ${imgStyle}
  `;
  // Lazy-load and async decode keep the bubble paint fast.
  img.loading = 'lazy';
  img.decoding = 'async';
  // Allow CORS so error events fire correctly on cross-origin CDN failures.
  img.referrerPolicy = 'no-referrer-when-downgrade';

  let cursor = 0;
  const advance = (): void => {
    cursor++;
    if (cursor >= chain.length) {
      // Chain exhausted (no terminal gradient marker — defensive). Show the
      // generic html5 fallback so we never end up with empty space.
      skeleton.remove();
      img.remove();
      wrapper.appendChild(renderBrandedFallback('html5', bubbleSize));
      return;
    }
    const next = chain[cursor];
    if (isGradientMarker(next)) {
      const platform = parseGradientMarker(next);
      skeleton.remove();
      img.remove();
      wrapper.appendChild(renderBrandedFallback(platform, bubbleSize));
      return;
    }
    // Try the next URL.
    img.src = next;
  };

  img.addEventListener('load', () => {
    // Hide skeleton on successful first paint of the chosen tier.
    skeleton.remove();
    img.style.opacity = '1';
  });
  img.addEventListener('error', advance);

  // Sanity-check the first URL through safeCssUrl-equivalent guard. We do it
  // by simply assigning to img.src — http(s) and data: image/* URLs are fine
  // for <img>, so we don't need the full CSS-context sanitiser here.
  img.src = chain[0];

  wrapper.appendChild(img);
  return wrapper;
}

// =============================================================================
// Bubble Component
// =============================================================================

export interface BubbleOptions {
  position: BubblePosition;
  styling: StylingConfig;
  video?: VideoConfig;
  onClick: () => void;
  onHover?: () => void;
  socialProof?: SocialProofConfig;
  showBranding?: boolean;
}

/**
 * Bubble trigger element
 */
export class BubbleTrigger {
  private container: HTMLElement | null = null;
  private button: HTMLButtonElement | null = null;
  private options: BubbleOptions;
  private isVisible = false;
  private previewTimeout: number | null = null;
  private hidePreviewTimeout: number | null = null;
  private preloaded = false;
  private videoPreviewEl: HTMLVideoElement | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private equalizerEl: HTMLElement | null = null;
  private hasPlayedAttentionSnap = false;

  constructor(options: BubbleOptions) {
    this.options = options;
  }

  /**
   * Render the bubble into the Shadow DOM
   */
  render(): HTMLElement {
    const shadow = getShadowContainer();
    if (!shadow) {
      throw new Error('Shadow DOM not initialized');
    }

    // Create container
    this.container = document.createElement('div');
    this.container.className = 'clickreel-trigger-container';
    this.container.setAttribute('role', 'complementary');
    this.container.setAttribute('aria-label', 'Video engagement widget');

    // Apply positioning and styles
    this.applyContainerStyles();

    // Start invisible — entrance animation will reveal via opacity transition
    this.container.style.opacity = '0';

    // Create the bubble button
    this.button = this.createButton();
    this.container.appendChild(this.button);

    // Add social proof badge below bubble if configured
    if (this.options.socialProof?.showOnBubble && this.options.socialProof.text) {
      const badge = this.createBubbleSocialProof(this.options.socialProof.text);
      this.container.appendChild(badge);
    }

    // Add thumbnail preview if video exists. The hover preview card uses the
    // same fallback chain as the bubble itself, so that even when the primary
    // CDN tier 404s, the hover preview still has a visible image or branded
    // fallback to render.
    if (this.options.video) {
      const chain = getThumbnailFallbackChain(this.options.video);
      // Only render the preview card when we actually have something useful
      // to show: either a real URL or a non-empty chain (which always has a
      // gradient marker terminator). Skip for videos with no video info.
      if (chain.length > 0) {
        const preview = this.createThumbnailPreview();
        this.container.appendChild(preview);
      }
    }

    // Add styles to Shadow DOM
    this.injectStyles(shadow);

    shadow.appendChild(this.container);

    // Set up lazy video preview with IntersectionObserver
    this.setupLazyVideoPreview();

    return this.container;
  }

  /**
   * Show the bubble with entrance animation
   */
  show(): void {
    if (!this.container || this.isVisible) return;

    this.container.classList.remove('hidden');

    // Remove any previous entrance animation class
    this.container.classList.remove(
      'clickreel-entrance-fade',
      'clickreel-entrance-slide-in',
      'clickreel-entrance-slide-in-left',
      'clickreel-entrance-bounce',
      'clickreel-entrance-grow',
      'clickreel-entrance-pulse-in',
      'clickreel-entrance-attention-snap',
    );

    const animation = this.options.styling.entranceAnimation || 'fade';

    // Attention-snap: multi-phase entrance (appear → shake → morph → settle)
    if (animation === 'attention-snap' && !this.hasPlayedAttentionSnap) {
      this.hasPlayedAttentionSnap = true;
      this.runAttentionSnap(this.options.styling.shakeIntensity || 'medium');
      return;
    }

    if (animation === 'slide-in') {
      const isLeft = this.options.position.includes('left');
      this.container.classList.add(isLeft ? 'clickreel-entrance-slide-in-left' : 'clickreel-entrance-slide-in');
    } else if (animation === 'attention-snap') {
      // After first play, subsequent shows use fade
      this.container.classList.add('clickreel-entrance-fade');
    } else {
      this.container.classList.add(`clickreel-entrance-${animation}`);
    }

    this.container.classList.add('visible');
    this.isVisible = true;
  }

  /**
   * Hide the bubble with animation
   */
  hide(): void {
    if (!this.container || !this.isVisible) return;

    this.container.classList.remove('visible');
    this.container.classList.add('hidden');
    this.isVisible = false;
  }

  /**
   * Remove the bubble from DOM
   */
  destroy(): void {
    if (this.previewTimeout) {
      clearTimeout(this.previewTimeout);
      // Sprint 006 (2026-04-29): null after clearTimeout to match the
      // hidePreviewTimeout pattern below. Without this, a GC-delayed callback
      // firing showPreview() would see a stale timer ID and the subsequent
      // clearTimeout would be a no-op, leaving a dangling timer reference.
      this.previewTimeout = null;
    }
    if (this.hidePreviewTimeout) {
      clearTimeout(this.hidePreviewTimeout);
      this.hidePreviewTimeout = null;
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    if (this.container) {
      this.container.remove();
      this.container = null;
      this.button = null;
    }
    this.videoPreviewEl = null;
    this.equalizerEl = null;
  }

  /**
   * Update bubble position
   */
  setPosition(position: BubblePosition): void {
    this.options.position = position;
    if (this.container) {
      this.applyContainerStyles();
    }
  }

  /**
   * Preload video assets based on video type (Feature 5)
   * - YouTube: inject YouTube IFrame API script if not already present
   * - MP4 videos: create a hidden <link rel="preload" as="video"> tag
   * - TikTok numeric IDs: no special preloading needed
   */
  startPreloading(): void {
    if (this.preloaded) return;
    this.preloaded = true;

    const video = this.options.video;
    if (!video) return;

    // YouTube: preload IFrame API via shared singleton loader
    if (video.youtubeId) {
      loadYouTubeApi();
      return;
    }

    // MP4 video: create a preload link
    const mp4Url = video.videoPreviewUrl || video.url;
    if (mp4Url && (mp4Url.endsWith('.mp4') || mp4Url.includes('.mp4'))) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'video';
      link.href = mp4Url;
      link.setAttribute('type', 'video/mp4');
      link.style.display = 'none';
      document.head.appendChild(link);
      return;
    }

    // TikTok numeric IDs: nothing special needed
  }

  /**
   * Play exit-intent pulse animation on the bubble button (Feature 6)
   * Applies the 'clickreel-exit-pulse' CSS class for 1.8s, then removes it.
   */
  pulse(): void {
    if (!this.button) return;

    this.button.classList.add('clickreel-exit-pulse');
    setTimeout(() => {
      this.button?.classList.remove('clickreel-exit-pulse');
    }, 1800);
  }

  /**
   * Show equalizer bars on the bubble (Feature 1)
   * Adds animated equalizer bars colored with the primary color from styling config.
   */
  showEqualizer(): void {
    if (!this.button || this.equalizerEl) return;

    const primaryColor = this.options.styling.primaryColor || '#6366f1';

    const eq = document.createElement('div');
    eq.className = 'clickreel-equalizer';

    for (let i = 0; i < 3; i++) {
      const bar = document.createElement('div');
      bar.className = 'clickreel-eq-bar';
      bar.style.backgroundColor = primaryColor;
      eq.appendChild(bar);
    }

    this.button.appendChild(eq);
    this.equalizerEl = eq;
  }

  /**
   * Hide/remove equalizer bars from the bubble (Feature 1)
   */
  hideEqualizer(): void {
    if (this.equalizerEl) {
      this.equalizerEl.remove();
      this.equalizerEl = null;
    }
  }

  /**
   * Get the bubble container element (for morph effects to apply spring bounce)
   */
  getElement(): HTMLElement | null {
    return this.container;
  }

  /**
   * Get the bubble's morph target coordinates for transition animations
   */
  getMorphTarget(): MorphTarget | null {
    if (!this.container) return null;
    const rect = this.container.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
      width: rect.width,
      height: rect.height,
      borderRadius: this.container.style.borderRadius ||
        window.getComputedStyle(this.button || this.container).borderRadius || '50%',
    };
  }

  // ===========================================================================
  // Private Methods
  // ===========================================================================

  /**
   * Snap Lab attention-grab entrance:
   * A phone-shaped bubble appears in the top-right corner, shakes to grab attention,
   * then morphs into a pill shape that settles into the bottom-right.
   *
   * Phase 1 (380ms): Phone bubble fades in at top-right
   * Phase 2 (1100ms): Shake with configurable intensity
   * Phase 3 (700ms): Morph to pill shape + translateY to bottom-right, swap content
   * Phase 4 (400ms): Settle bounce, become interactive
   */
  private runAttentionSnap(intensity: ShakeIntensity): void {
    if (!this.container || !this.button) return;

    ensureGlobalMorphKeyframes();

    const container = this.container;
    const button = this.button;
    const margin = isMobile() ? 16 : 24;
    const userShape = this.options.styling.bubbleShape;
    const settledShape: 'circle' | 'pill' | 'phone' =
      userShape === 'circle' ? 'circle' : userShape === 'phone' ? 'phone' : 'pill';

    // Phone dimensions (starting state — always phone; also the final state
    // when settledShape === 'phone').
    const phoneW = 90;
    const phoneH = 180;
    const phoneRadius = '22px';

    // Final settled dimensions based on shape
    const circleSize = this.getBubbleSize(this.options.styling.bubbleSize);
    const finalW = settledShape === 'circle' ? circleSize : settledShape === 'phone' ? phoneW : 174;
    const finalH = settledShape === 'circle' ? circleSize : settledShape === 'phone' ? phoneH : 50;
    const finalRadius = settledShape === 'circle' ? '50%' : settledShape === 'phone' ? phoneRadius : '25px';

    // Calculate translateY to move from top-right to bottom-right
    const translateY = window.innerHeight - margin * 2 - finalH;

    // Sprint 001: build the full fallback chain so we never end up with an
    // empty phone screen. The chain always terminates in a branded gradient.
    const thumbnailChain = getThumbnailFallbackChain(this.options.video);

    // --- Phase 0: Set up as phone shape at top-right, invisible ---
    container.style.cssText = `
      position: fixed;
      top: ${margin}px;
      right: ${margin}px;
      bottom: auto;
      left: auto;
      z-index: 1;
      width: ${phoneW}px;
      height: ${phoneH}px;
      pointer-events: auto;
      opacity: 0;
    `;

    // Render phone view into the button
    button.style.cssText = `
      width: ${phoneW}px;
      height: ${phoneH}px;
      border-radius: ${phoneRadius};
      background: #0f0f0f;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.07);
      cursor: pointer;
      overflow: hidden;
      position: relative;
      border: none;
      padding: 0;
      transition: none;
    `;
    button.innerHTML = '';

    // Thumbnail fills the phone frame — uses <img> with onerror cascade so a
    // missing CDN tier never leaves the bubble with bare gradient + dead space.
    button.appendChild(
      renderThumbnailWithFallback({
        chain: thumbnailChain,
        bubbleSize: phoneW,
        alt: this.options.video?.title || 'Video preview',
      }),
    );

    // Cinematic overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = `position:absolute;inset:0;background:linear-gradient(to bottom,rgba(0,0,0,0.45) 0%,rgba(0,0,0,0.05) 35%,rgba(0,0,0,0.05) 60%,rgba(0,0,0,0.55) 100%);z-index:2;`;
    button.appendChild(overlay);

    // Dynamic island
    const island = document.createElement('div');
    island.style.cssText = `position:absolute;top:10px;left:50%;transform:translateX(-50%);width:36px;height:9px;border-radius:5px;background:#0f0f0f;z-index:3;`;
    button.appendChild(island);

    // Play button
    const playBtn = document.createElement('div');
    playBtn.style.cssText = `position:relative;z-index:3;width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,0.88);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 16px rgba(0,0,0,0.5);`;
    playBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="#0f0f0f"><polygon points="9,5 20,12 9,19"/></svg>`;
    button.appendChild(playBtn);

    // "TAP TO WATCH" label
    const tapLabel = document.createElement('div');
    tapLabel.style.cssText = `position:absolute;bottom:10px;left:0;right:0;text-align:center;font-size:8px;font-weight:800;color:rgba(255,255,255,0.75);letter-spacing:0.12em;font-family:system-ui,sans-serif;z-index:3;`;
    tapLabel.textContent = 'TAP TO WATCH';
    button.appendChild(tapLabel);

    // Pulsing ring
    const ring = document.createElement('div');
    ring.style.cssText = `position:absolute;inset:-7px;border-radius:28px;border:2px solid rgba(255,255,255,0.22);animation:attsnap-pulse-ring 1.9s ease-in-out infinite;pointer-events:none;z-index:0;`;
    button.appendChild(ring);

    container.classList.add('visible', 'clickreel-entrance-attention-snap');
    this.isVisible = true;

    // --- Phase 1: Appear (380ms) ---
    // Double-rAF ensures browser has painted + parsed injected keyframes before animation starts
    requestAnimationFrame(() => { requestAnimationFrame(() => {
      container.style.animation = 'attsnap-appear 380ms cubic-bezier(0.16, 1, 0.3, 1) forwards';

      // --- Phase 2: Shake (starts at 400ms, lasts 1100ms) ---
      setTimeout(() => {
        container.style.opacity = '1';
        // Pre-promote to compositor layer during the shake so Phase 3's morph
        // starts on an already-established layer. Prevents a frame of blank/
        // flicker on heavy pages (e.g. Shopify themes) where same-tick layer
        // promotion + transition start can't complete inside one compositor frame.
        container.style.willChange = 'transform';
        button.style.willChange = 'transform';
        container.style.animation = `attsnap-shake-${intensity} 1.1s ease-in-out`;

        // --- Phase 3: Morph to final shape + move to bottom-right ---
        setTimeout(() => {
          container.style.animation = 'none';

          // One rAF gate lets the browser commit the shake-animation removal
          // before the morph transition starts, so the first frame of the
          // transition renders on a settled compositor layer (already promoted
          // in Phase 2). This eliminates the Shopify-theme flicker where a
          // same-tick promotion + transition race produces a blank frame.
          requestAnimationFrame(() => {
            // Keep will-change narrow to `transform` only. Non-compositor
            // properties (width/height/border-radius/background/box-shadow)
            // don't benefit from will-change and actively harm layer efficiency.
            container.style.willChange = 'transform';
            button.style.willChange = 'transform';

            const morphEasing = 'cubic-bezier(0.16, 1, 0.3, 1)';
            const morphDuration = '700ms';
            const morphTransition = `width ${morphDuration} ${morphEasing}, height ${morphDuration} ${morphEasing}, transform ${morphDuration} ${morphEasing}, border-radius ${morphDuration} ${morphEasing}`;
            container.style.transition = morphTransition;
            container.style.width = `${finalW}px`;
            container.style.height = `${finalH}px`;
            container.style.transform = `translateY(${translateY}px)`;

            button.style.transition = `width ${morphDuration} ${morphEasing}, height ${morphDuration} ${morphEasing}, border-radius ${morphDuration} ${morphEasing}, background ${morphDuration} ${morphEasing}, box-shadow ${morphDuration} ${morphEasing}`;
            button.style.width = `${finalW}px`;
            button.style.height = `${finalH}px`;
            button.style.borderRadius = finalRadius;
            button.style.background =
              settledShape === 'circle' ? 'transparent'
              : settledShape === 'phone' ? '#0f0f0f'
              : 'rgba(15,15,15,0.92)';
            button.style.boxShadow =
              settledShape === 'circle' ? '0 4px 20px rgba(0,0,0,0.35), 0 0 0 3px rgba(255,255,255,0.9)'
              : settledShape === 'phone' ? '0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.07)'
              : '0 6px 28px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.1)';

            // Fade out phone content during morph — but only when we're
            // actually swapping to a different UI (circle/pill). For phone
            // settled shape, the starting phone content is the final state,
            // so keep it fully visible.
            if (settledShape !== 'phone') {
              const phoneContent = button.querySelectorAll('div, svg');
              phoneContent.forEach((el) => {
                (el as HTMLElement).style.transition = 'opacity 300ms ease';
                (el as HTMLElement).style.opacity = '0';
              });
            }

            // --- Phase 4: Settled — crossfade to final shape content ---
            setTimeout(() => {
              container.style.transition = 'none';
              container.style.transform = 'none';
              container.style.top = 'auto';
              container.style.bottom = `${margin}px`;
              button.style.transition = 'none';

              // Phone settled shape: existing phone content (thumbnail, island,
              // play button, TAP TO WATCH, pulse ring) is already the final
              // UI. Skip the crossfade/settled-overlay build entirely — just
              // run the settle bounce and release compositor layers.
              if (settledShape === 'phone') {
                container.style.animation = 'attsnap-settled-bounce 400ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards';
                setTimeout(() => {
                  container.style.animation = '';
                  container.style.transition = '';
                  container.style.willChange = 'auto';
                  button.style.willChange = 'auto';
                }, 400);
                return;
              }

              // Build settled content in an overlay (invisible initially)
              const settledOverlay = document.createElement('div');
              settledOverlay.style.cssText = `position:absolute;inset:0;display:flex;align-items:center;opacity:0;transition:opacity 300ms ease;border-radius:inherit;overflow:hidden;z-index:10;`;

              if (settledShape === 'pill') {
                settledOverlay.style.padding = '0 16px 0 9px';
                settledOverlay.style.gap = '10px';
                settledOverlay.style.background = 'rgba(15,15,15,0.92)';

                const thumbCircle = document.createElement('div');
                thumbCircle.style.cssText = `width:34px;height:34px;border-radius:50%;overflow:hidden;flex-shrink:0;background:#1a1a1a;border:1.5px solid rgba(255,255,255,0.18);position:relative;`;
                thumbCircle.appendChild(
                  renderThumbnailWithFallback({
                    chain: thumbnailChain,
                    bubbleSize: 34,
                    alt: 'Video',
                  }),
                );
                settledOverlay.appendChild(thumbCircle);

                const watchLabel = document.createElement('span');
                watchLabel.style.cssText = 'color:white;font-size:14px;font-weight:700;font-family:system-ui,sans-serif;white-space:nowrap;flex:1;';
                watchLabel.textContent = 'Watch Now';
                settledOverlay.appendChild(watchLabel);

                const liveDot = document.createElement('div');
                liveDot.style.cssText = 'width:7px;height:7px;border-radius:50%;background:#ff3b3b;box-shadow:0 0 6px rgba(255,59,59,0.6);flex-shrink:0;';
                settledOverlay.appendChild(liveDot);
              } else {
                settledOverlay.style.justifyContent = 'center';

                settledOverlay.appendChild(
                  renderThumbnailWithFallback({
                    chain: thumbnailChain,
                    bubbleSize: finalW,
                    alt: this.options.video?.title || 'Video preview',
                  }),
                );

                const playOverlay = document.createElement('div');
                playOverlay.className = 'clickreel-play-overlay';
                playOverlay.style.cssText = `position:absolute;bottom:6px;right:6px;width:20px;height:20px;background:rgba(0,0,0,0.5);border-radius:50%;display:flex;align-items:center;justify-content:center;z-index:2;border:1px solid rgba(255,255,255,0.3);`;
                playOverlay.innerHTML = `<svg width="8" height="8" viewBox="0 0 24 24" fill="white"><path d="M8 5.14v13.72c0 .89.97 1.44 1.71.97l10.49-6.86a1.16 1.16 0 000-1.94L9.71 4.17A1.16 1.16 0 008 5.14z"/></svg>`;
                settledOverlay.appendChild(playOverlay);

                const circleRing = document.createElement('div');
                circleRing.className = 'clickreel-pulse-ring';
                settledOverlay.appendChild(circleRing);
              }

              // Append settled overlay on top of existing phone content, then crossfade
              button.appendChild(settledOverlay);
              requestAnimationFrame(() => {
                settledOverlay.style.opacity = '1';
              });

              // After crossfade completes, remove old phone content and flatten
              setTimeout(() => {
                // Remove old phone-mode children; keep only the settled overlay
                const children = Array.from(button.children);
                children.forEach((child) => {
                  if (child !== settledOverlay) button.removeChild(child);
                });
                settledOverlay.style.transition = 'none';
                settledOverlay.style.opacity = '1';

                if (settledShape === 'pill') {
                  // Pill's in-flow flex children (thumbCircle, watchLabel,
                  // liveDot) give the relative wrapper intrinsic size, so we
                  // can flatten it into the button's own flex layout and
                  // apply padding/gap at the button level.
                  settledOverlay.style.position = 'relative';
                  settledOverlay.style.inset = 'auto';
                  button.style.display = 'flex';
                  button.style.alignItems = 'center';
                  button.style.padding = '0 16px 0 9px';
                  button.style.gap = '10px';
                }
                // Circle: leave settledOverlay as absolute fill (inset:0). Its
                // children (thumb, playOverlay, circleRing) are all absolutely
                // positioned — flattening to position:relative would collapse
                // the wrapper to 0x0 and blank the thumbnail.
              }, 350);

              // Settle bounce
              container.style.animation = 'attsnap-settled-bounce 400ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards';
              setTimeout(() => {
                container.style.animation = '';
                container.style.transition = '';
                // Release compositor layers
                container.style.willChange = 'auto';
                button.style.willChange = 'auto';
              }, 400);
            }, 700);
          });
        }, 1100);
      }, 400);
    }); });
  }

  private applyContainerStyles(): void {
    if (!this.container) return;

    const { position, styling } = this.options;
    const margin = isMobile() ? '16px' : '24px';
    const isPhone = styling.bubbleShape === 'phone';

    let width: number;
    let height: number;

    if (isPhone) {
      width = isMobile() ? 64 : 80;
      height = isMobile() ? 128 : 160;
    } else {
      const size = this.getBubbleSize(styling.bubbleSize);
      width = size;
      height = styling.bubbleShape === 'pill' ? Math.round(size * 0.6) : size;
    }

    // Base styles
    let cssText = `
      position: fixed;
      pointer-events: auto;
      z-index: 1;
      width: ${width}px;
      height: ${height}px;
    `;

    // Position styles
    switch (position) {
      case 'bottom-right':
        cssText += `bottom: ${margin}; right: ${margin};`;
        break;
      case 'bottom-left':
        cssText += `bottom: ${margin}; left: ${margin};`;
        break;
      case 'top-right':
        cssText += `top: ${margin}; right: ${margin};`;
        break;
      case 'top-left':
        cssText += `top: ${margin}; left: ${margin};`;
        break;
    }

    this.container.style.cssText = cssText;
  }

  private createButton(): HTMLButtonElement {
    const button = document.createElement('button');
    const { styling, video } = this.options;
    const size = this.getBubbleSize(styling.bubbleSize);
    const isPill = styling.bubbleShape === 'pill';

    button.className = 'clickreel-button';
    button.setAttribute('aria-label', 'Play video');
    button.setAttribute('aria-haspopup', 'dialog');

    // Check for video preview sources (priority: videoPreviewUrl > thumbnail > YouTube > branded)
    const videoPreviewUrl = video?.videoPreviewUrl;
    // Sprint 001 (2026-04-29): instead of a single thumbnailUrl with no error
    // recovery, build the full fallback chain. The chain ALWAYS terminates in
    // a `gradient:<platform>` marker, so it has length>=1 even for videos
    // with no real thumbnail. We use chain.length > 1 (or chain[0] is a real
    // URL) to decide whether to enter the "has thumbnail" branch.
    const thumbnailChain = getThumbnailFallbackChain(video);
    const hasRealThumbnail = thumbnailChain.length > 0 && !isGradientMarker(thumbnailChain[0]);
    const hasVideoPreview = !!videoPreviewUrl || hasRealThumbnail;
    const isInstagramVideo = !!(video?.instagramPostUrl || video?.instagramVideoUrl);
    const isTikTokVideo = !!video?.tiktokId;

    // Phone shape rendering path
    if (styling.bubbleShape === 'phone') {
      const phoneW = isMobile() ? 64 : 80;
      const phoneH = isMobile() ? 128 : 160;
      const radius = isMobile() ? 12 : 16;

      button.style.cssText = `
        width: ${phoneW}px;
        height: ${phoneH}px;
        border-radius: ${radius}px;
        background: #000;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.15);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        cursor: pointer;
        overflow: hidden;
        position: relative;
        border: none;
        padding: 0;
      `;

      // Dynamic island
      const island = document.createElement('div');
      island.className = 'clickreel-phone-island';
      island.style.cssText = `
        position: absolute;
        top: 6px;
        left: 50%;
        transform: translateX(-50%);
        width: 24px;
        height: 6px;
        background: #1a1a1a;
        border-radius: 3px;
        z-index: 3;
      `;
      button.appendChild(island);

      // Video thumbnail fills the phone frame. The chain has at least one
      // entry (terminal gradient marker), so we always render SOMETHING here.
      // When videoPreviewUrl is set, the wrapper is also tagged so the
      // hover-handler can fade between MP4 preview and the placeholder.
      const phoneThumbWrapper = renderThumbnailWithFallback({
        chain: thumbnailChain,
        bubbleSize: phoneW,
        alt: video?.title || 'Video preview',
      });
      if (videoPreviewUrl) {
        phoneThumbWrapper.classList.add('clickreel-video-placeholder');
      }
      button.appendChild(phoneThumbWrapper);

      // Play overlay centered at bottom
      const playOverlay = document.createElement('div');
      playOverlay.className = 'clickreel-play-overlay';
      playOverlay.style.cssText = `
        position: absolute;
        bottom: 10px;
        left: 50%;
        transform: translateX(-50%);
        width: 24px;
        height: 24px;
        background: rgba(0, 0, 0, 0.5);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2;
        border: 1px solid rgba(255, 255, 255, 0.3);
      `;
      playOverlay.innerHTML = `
        <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
          <path d="M8 5.14v13.72c0 .89.97 1.44 1.71.97l10.49-6.86a1.16 1.16 0 000-1.94L9.71 4.17A1.16 1.16 0 008 5.14z"/>
        </svg>
      `;
      button.appendChild(playOverlay);

      // Pulse ring (phone shape)
      const pulseRing = document.createElement('div');
      pulseRing.className = 'clickreel-pulse-ring clickreel-pulse-ring-phone';
      button.appendChild(pulseRing);

    } else if (hasVideoPreview) {
      button.style.cssText = `
        width: ${size}px;
        height: ${isPill ? size * 0.6 : size}px;
        border-radius: ${isPill ? `${size / 3}px` : '50%'};
        background: transparent;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35), 0 0 0 3px rgba(255, 255, 255, 0.9);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        cursor: pointer;
        overflow: hidden;
        position: relative;
        border: none;
        padding: 0;
      `;

      // Lazy video preview: do NOT eagerly create the video element.
      // Instead, use a static thumbnail (with onerror cascade) as the initial
      // placeholder; the MP4 video element is created by the
      // IntersectionObserver callback when the bubble enters the viewport.
      // For non-preview bubbles, the same wrapper is the sole image layer.
      const placeholderWrapper = renderThumbnailWithFallback({
        chain: thumbnailChain,
        bubbleSize: size,
        alt: video?.title || 'Video preview',
        imgStyle: 'transition: opacity 0.2s ease;',
      });
      if (videoPreviewUrl) {
        placeholderWrapper.classList.add('clickreel-video-placeholder');
      }
      button.appendChild(placeholderWrapper);

      // Create smaller play button overlay (bottom-right corner)
      const playOverlay = document.createElement('div');
      playOverlay.className = 'clickreel-play-overlay';
      playOverlay.style.cssText = `
        position: absolute;
        bottom: 6px;
        right: 6px;
        width: 20px;
        height: 20px;
        background: rgba(0, 0, 0, 0.5);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2;
        border: 1px solid rgba(255, 255, 255, 0.3);
      `;
      playOverlay.innerHTML = `
        <svg width="8" height="8" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 5.14v13.72c0 .89.97 1.44 1.71.97l10.49-6.86a1.16 1.16 0 000-1.94L9.71 4.17A1.16 1.16 0 008 5.14z"/>
        </svg>
      `;
      button.appendChild(playOverlay);

      // Add pulsing ring animation
      const pulseRing = document.createElement('div');
      pulseRing.className = 'clickreel-pulse-ring';
      button.appendChild(pulseRing);

    } else if (thumbnailChain.length > 0 && isGradientMarker(thumbnailChain[0])) {
      // Sprint 001: generalised branded gradient bubble for ANY platform when
      // no thumbnail URL is available. Previously only Instagram/TikTok had
      // this fallback — YouTube/HTML5/Cloudflare campaigns with missing
      // thumbnails would render as an empty circle outline. The branded
      // gradient + platform icon ensures the bubble is never visually empty.
      const platform = parseGradientMarker(thumbnailChain[0]);
      const { gradient, shadow } = getBrandedFallbackStyles(platform);

      button.style.cssText = `
        width: ${size}px;
        height: ${isPill ? size * 0.6 : size}px;
        border-radius: ${isPill ? `${size / 3}px` : '50%'};
        background: ${gradient};
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 20px ${shadow}, 0 0 0 3px rgba(255, 255, 255, 0.9);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        cursor: pointer;
        overflow: hidden;
        position: relative;
        border: none;
        padding: 0;
      `;

      // Platform icon
      const brandIcon = document.createElement('div');
      brandIcon.style.cssText = `
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1;
        opacity: 0.9;
      `;
      brandIcon.innerHTML = getBrandedFallbackIcon(platform, size);
      button.appendChild(brandIcon);

      // Small play button overlay (bottom-right)
      const playOverlay = document.createElement('div');
      playOverlay.className = 'clickreel-play-overlay';
      playOverlay.style.cssText = `
        position: absolute;
        bottom: 6px;
        right: 6px;
        width: 20px;
        height: 20px;
        background: rgba(0, 0, 0, 0.5);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2;
        border: 1px solid rgba(255, 255, 255, 0.3);
      `;
      playOverlay.innerHTML = `
        <svg width="8" height="8" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 5.14v13.72c0 .89.97 1.44 1.71.97l10.49-6.86a1.16 1.16 0 000-1.94L9.71 4.17A1.16 1.16 0 008 5.14z"/>
        </svg>
      `;
      button.appendChild(playOverlay);

      // Pulsing ring animation
      const pulseRing = document.createElement('div');
      pulseRing.className = 'clickreel-pulse-ring';
      button.appendChild(pulseRing);
    } else {
      // Fallback to solid color button
      button.style.cssText = `
        width: ${size}px;
        height: ${isPill ? size * 0.6 : size}px;
        border-radius: ${isPill ? `${size / 3}px` : '50%'};
        background: ${styling.primaryColor || '#6366f1'};
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        cursor: pointer;
        border: none;
      `;
      button.innerHTML = this.getPlayIcon();
    }

    // Event listeners
    button.addEventListener('click', (e) => {
      e.stopPropagation();
      this.options.onClick();
    });

    const hasBubbleRing = hasVideoPreview || isInstagramVideo || isTikTokVideo;
    button.addEventListener('mouseenter', () => {
      button.style.transform = 'scale(1.1)';
      button.style.boxShadow = hasBubbleRing
        ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 4px rgba(255, 255, 255, 1)'
        : '0 6px 24px rgba(0, 0, 0, 0.3)';
      this.handleVideoPreviewHover(true);
      this.showPreview();
      this.options.onHover?.();
    });

    button.addEventListener('mouseleave', () => {
      button.style.transform = 'scale(1)';
      button.style.boxShadow = hasBubbleRing
        ? '0 4px 20px rgba(0, 0, 0, 0.35), 0 0 0 3px rgba(255, 255, 255, 0.9)'
        : '0 4px 20px rgba(0, 0, 0, 0.25)';
      this.handleVideoPreviewHover(false);
      this.hidePreview();
    });

    // Touch events for mobile
    button.addEventListener('touchstart', (e) => {
      e.stopPropagation();
      button.style.transform = 'scale(0.95)';
    }, { passive: true });

    button.addEventListener('touchend', () => {
      button.style.transform = 'scale(1)';
    }, { passive: true });

    return button;
  }

  /**
   * Set up IntersectionObserver for lazy video preview loading (Feature 2).
   * Only creates the video element when the bubble container enters viewport.
   */
  private setupLazyVideoPreview(): void {
    const videoPreviewUrl = this.options.video?.videoPreviewUrl;
    if (!videoPreviewUrl || !this.container) return;

    // Only set up if IntersectionObserver is available
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: create video immediately for browsers without IO
      this.createLazyVideoPreview(videoPreviewUrl);
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.createLazyVideoPreview(videoPreviewUrl);
            // Disconnect after first intersection
            this.intersectionObserver?.disconnect();
            this.intersectionObserver = null;
            break;
          }
        }
      },
      { threshold: 0.1 },
    );

    this.intersectionObserver.observe(this.container);
  }

  /**
   * Create the video preview element lazily (called by IntersectionObserver).
   * Starts with preload="none", then switches to preload="metadata".
   */
  private createLazyVideoPreview(videoUrl: string): void {
    if (!this.button || this.videoPreviewEl) return;

    const video = document.createElement('video');
    video.className = 'clickreel-video-preview';
    video.src = videoUrl;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('disablePictureInPicture', 'true');
    video.preload = 'none';
    video.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: inherit;
      z-index: 1;
      opacity: 0;
      transition: opacity 0.2s ease;
    `;

    // Switch to metadata preload now that it's in viewport
    video.preload = 'metadata';

    this.button.appendChild(video);
    this.videoPreviewEl = video;
  }

  /**
   * Handle hover/unhover for lazy video preview (Feature 2).
   * On hover: set preload="auto", play silently, fade in, scale 1.02.
   * On unhover: pause, reset to beginning, fade out, scale 1.0.
   */
  private handleVideoPreviewHover(isHovering: boolean): void {
    if (!this.videoPreviewEl) return;

    if (isHovering) {
      this.videoPreviewEl.preload = 'auto';
      this.videoPreviewEl.style.opacity = '1';
      this.videoPreviewEl.style.transform = 'scale(1.02)';
      this.videoPreviewEl.play().catch(() => {
        // Autoplay may be blocked; silently ignore
      });

      // Fade out the static placeholder if present
      const placeholder = this.button?.querySelector('.clickreel-video-placeholder') as HTMLElement;
      if (placeholder) {
        placeholder.style.opacity = '0';
      }
    } else {
      this.videoPreviewEl.pause();
      this.videoPreviewEl.currentTime = 0;
      this.videoPreviewEl.style.opacity = '0';
      this.videoPreviewEl.style.transform = 'scale(1.0)';

      // Restore the static placeholder if present
      const placeholder = this.button?.querySelector('.clickreel-video-placeholder') as HTMLElement;
      if (placeholder) {
        placeholder.style.opacity = '1';
      }
    }
  }

  /**
   * Create a social proof badge element below the bubble (Feature 8).
   */
  private createBubbleSocialProof(text: string): HTMLElement {
    const badge = document.createElement('div');
    badge.className = 'clickreel-bubble-social-proof';

    // Truncate to ~20 characters
    const truncated = text.length > 20 ? text.substring(0, 20) + '\u2026' : text;
    badge.textContent = truncated;

    return badge;
  }

  private createThumbnailPreview(): HTMLElement {
    const preview = document.createElement('div');
    preview.className = 'clickreel-preview hidden';

    const { position, video, styling } = this.options;
    const isLeft = position.includes('left');
    const isTop = position.includes('top');
    const size = this.getBubbleSize(styling.bubbleSize);

    preview.style.cssText = `
      position: absolute;
      ${isTop ? 'top' : 'bottom'}: ${size + 10}px;
      ${isLeft ? 'left' : 'right'}: 0;
      width: 200px;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      opacity: 0;
      transform: translateY(${isTop ? '-10px' : '10px'}) scale(0.95);
      transition: opacity 0.2s ease, transform 0.2s ease;
      pointer-events: none;
      aspect-ratio: 16 / 9;
      background: #000;
    `;

    // Sprint 001: hover preview card uses the same fallback chain so that a
    // 404 on the primary CDN tier still produces a visible preview rather
    // than a broken image icon.
    const chain = getThumbnailFallbackChain(video);
    const thumbWrapper = renderThumbnailWithFallback({
      chain,
      bubbleSize: 200,
      alt: video?.title || 'Video preview',
    });
    preview.appendChild(thumbWrapper);

    // Title overlay
    if (video?.title) {
      const title = document.createElement('div');
      title.textContent = video.title;
      title.style.cssText = `
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 12px;
        background: linear-gradient(transparent, rgba(0,0,0,0.8));
        color: white;
        font-size: 14px;
        font-weight: 500;
        z-index: 2;
      `;
      preview.appendChild(title);
    }

    return preview;
  }

  private showPreview(): void {
    if (!this.container) return;

    const preview = this.container.querySelector('.clickreel-preview') as HTMLElement;
    if (!preview) return;

    // Delay showing preview slightly
    this.previewTimeout = window.setTimeout(() => {
      preview.classList.remove('hidden');
      preview.style.opacity = '1';
      preview.style.transform = 'translateY(0) scale(1)';
    }, 200);
  }

  private hidePreview(): void {
    if (this.previewTimeout) {
      clearTimeout(this.previewTimeout);
      this.previewTimeout = null;
    }

    if (!this.container) return;

    const preview = this.container.querySelector('.clickreel-preview') as HTMLElement;
    if (!preview) return;

    const isTop = this.options.position.includes('top');
    preview.style.opacity = '0';
    preview.style.transform = `translateY(${isTop ? '-10px' : '10px'}) scale(0.95)`;

    this.hidePreviewTimeout = window.setTimeout(() => {
      this.hidePreviewTimeout = null;
      preview.classList.add('hidden');
    }, 200);
  }

  private getBubbleSize(size?: 'small' | 'medium' | 'large'): number {
    if (isMobile()) {
      return size === 'large' ? 64 : size === 'small' ? 48 : 56;
    }
    return size === 'large' ? 72 : size === 'small' ? 52 : 60;
  }

  private getPlayIcon(): string {
    return `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 5.14v13.72c0 .89.97 1.44 1.71.97l10.49-6.86a1.16 1.16 0 000-1.94L9.71 4.17A1.16 1.16 0 008 5.14z"/>
      </svg>
    `;
  }

  private injectStyles(shadow: ShadowRoot): void {
    // Inject shared utility styles (equalizer, exit pulse, social proof badge, etc.)
    injectUtilityStyles(shadow);

    const existingStyle = shadow.querySelector('style[data-bubble]');
    if (existingStyle) return;

    const style = document.createElement('style');
    style.setAttribute('data-bubble', 'true');
    style.textContent = `
      .clickreel-trigger-container {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }

      /* Entrance animations */
      .clickreel-trigger-container.visible.clickreel-entrance-fade {
        animation: clickreel-fade-in 0.3s ease forwards;
      }
      .clickreel-trigger-container.visible.clickreel-entrance-slide-in {
        animation: clickreel-slide-from-right 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
      }
      .clickreel-trigger-container.visible.clickreel-entrance-slide-in-left {
        animation: clickreel-slide-from-left 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
      }
      .clickreel-trigger-container.visible.clickreel-entrance-bounce {
        animation: clickreel-bounce-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
      }
      .clickreel-trigger-container.visible.clickreel-entrance-grow {
        animation: clickreel-grow-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
      }
      .clickreel-trigger-container.visible.clickreel-entrance-pulse-in {
        animation: clickreel-pulse-in 0.6s ease forwards;
      }
      .clickreel-trigger-container.visible.clickreel-entrance-attention-snap {
        /* Animations handled programmatically in runAttentionSnap */
        animation: none;
      }
      /* Fallback for .visible without entrance class */
      .clickreel-trigger-container.visible:not([class*="clickreel-entrance-"]) {
        animation: clickreel-fade-in 0.3s ease forwards;
      }

      .clickreel-trigger-container.hidden {
        animation: clickreel-fade-out 0.2s ease forwards;
        pointer-events: none;
      }

      /* Phone shape pulse ring */
      .clickreel-pulse-ring-phone {
        border-radius: 16px !important;
      }

      .clickreel-button {
        position: relative;
      }

      .clickreel-button:focus-visible {
        outline: 3px solid white;
        outline-offset: 3px;
      }

      /* Pulsing ring animation for video thumbnail bubble */
      .clickreel-pulse-ring {
        position: absolute;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        border-radius: 50%;
        border: 2px solid rgba(255, 255, 255, 0.8);
        z-index: 0;
        animation: clickreel-pulse 2s ease-in-out infinite;
      }

      @keyframes clickreel-pulse {
        0%, 100% {
          transform: scale(1);
          opacity: 0.8;
        }
        50% {
          transform: scale(1.15);
          opacity: 0;
        }
      }

      /* Attention-snap entrance keyframes */
      @keyframes attsnap-appear {
        from { opacity: 0; transform: scale(0.72); }
        to   { opacity: 1; transform: scale(1); }
      }
      @keyframes attsnap-shake-mild {
        0%, 100% { transform: rotate(0deg) translateX(0); }
        10% { transform: rotate(-2deg) translateX(-2px); }
        20% { transform: rotate(2deg) translateX(2px); }
        30% { transform: rotate(-3deg) translateX(-3px); }
        40% { transform: rotate(3deg) translateX(3px); }
        50% { transform: rotate(-2deg) translateX(-2px); }
        60% { transform: rotate(2deg) translateX(2px); }
        70% { transform: rotate(-1deg) translateX(-1px); }
        80% { transform: rotate(1deg) translateX(1px); }
        90% { transform: rotate(0deg); }
      }
      @keyframes attsnap-shake-medium {
        0%, 100% { transform: rotate(0deg) translateX(0); }
        10% { transform: rotate(-5deg) translateX(-4px); }
        20% { transform: rotate(6deg) translateX(5px); }
        30% { transform: rotate(-8deg) translateX(-7px); }
        40% { transform: rotate(8deg) translateX(7px); }
        50% { transform: rotate(-6deg) translateX(-5px); }
        60% { transform: rotate(5deg) translateX(4px); }
        70% { transform: rotate(-3deg) translateX(-3px); }
        80% { transform: rotate(2deg) translateX(2px); }
        90% { transform: rotate(-1deg); }
      }
      @keyframes attsnap-shake-wild {
        0%, 100% { transform: rotate(0deg) translateX(0) scale(1); }
        8%  { transform: rotate(-10deg) translateX(-8px) scale(1.04); }
        16% { transform: rotate(13deg)  translateX(10px) scale(1.06); }
        24% { transform: rotate(-14deg) translateX(-11px) scale(1.05); }
        32% { transform: rotate(14deg)  translateX(11px) scale(1.06); }
        40% { transform: rotate(-11deg) translateX(-8px) scale(1.03); }
        48% { transform: rotate(9deg)   translateX(7px)  scale(1.02); }
        56% { transform: rotate(-6deg)  translateX(-5px) scale(1.01); }
        64% { transform: rotate(5deg)   translateX(4px); }
        72% { transform: rotate(-3deg)  translateX(-2px); }
        80% { transform: rotate(2deg)   translateX(1px); }
        90% { transform: rotate(-1deg); }
      }
      @keyframes attsnap-pulse-ring {
        0%   { opacity: 0.6; transform: scale(1); }
        50%  { opacity: 0.15; transform: scale(1.1); }
        100% { opacity: 0.6; transform: scale(1); }
      }
      @keyframes attsnap-settled-bounce {
        0%   { transform: scale(1); }
        30%  { transform: scale(1.08); }
        60%  { transform: scale(0.96); }
        80%  { transform: scale(1.02); }
        100% { transform: scale(1); }
      }

      @media (prefers-reduced-motion: reduce) {
        .clickreel-trigger-container {
          animation: none !important;
          transition: none !important;
        }
      }

      /* Play overlay hover effect */
      .clickreel-button:hover .clickreel-play-overlay {
        background: rgba(0, 0, 0, 0.7);
        transform: scale(1.1);
      }

      .clickreel-play-overlay {
        transition: background 0.2s ease, transform 0.2s ease;
      }

      /* Video preview element */
      .clickreel-video-preview {
        pointer-events: none;
      }

      .clickreel-preview.hidden {
        display: none;
      }

      /* Sprint 001 (2026-04-29): thumbnail skeleton + branded fallback layer.
         The skeleton fills the thumbnail wrapper from container paint until
         either (a) the first <img> load event fires or (b) the chain
         exhausts and we swap to the branded gradient. Eliminates the
         "transparent gap" + "empty bubble outline" failure mode. */
      .clickreel-thumbnail-wrapper {
        position: absolute;
        inset: 0;
        border-radius: inherit;
        overflow: hidden;
      }
      .clickreel-thumbnail-skeleton {
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: linear-gradient(
          90deg,
          rgba(40, 40, 50, 0.85) 0%,
          rgba(60, 60, 75, 0.85) 50%,
          rgba(40, 40, 50, 0.85) 100%
        );
        background-size: 200% 100%;
        animation: clickreel-thumbnail-shimmer 1.4s ease-in-out infinite;
        z-index: 0;
      }
      @keyframes clickreel-thumbnail-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      .clickreel-branded-fallback {
        position: absolute;
        inset: 0;
        border-radius: inherit;
        z-index: 1;
        animation: clickreel-thumbnail-fade-in 180ms ease;
      }
      @keyframes clickreel-thumbnail-fade-in {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      @media (prefers-reduced-motion: reduce) {
        .clickreel-thumbnail-skeleton {
          animation: none;
        }
        .clickreel-branded-fallback {
          animation: none;
        }
      }

      /* Mobile adjustments */
      @media (max-width: 767px) {
        .clickreel-preview {
          display: none !important;
        }
      }
    `;
    shadow.appendChild(style);
  }
}
